import React, { useState, useEffect } from "react";
import "./RestaurantDetailsModal.css";
import RejectApplicationModal from "./RejectApplicationModal";
import close from "../../Assets/close.svg";
import { useTLAction, useTLUpdateRestaurant } from "../../hooks/useApplications";

// ================= DOCUMENT VALIDATORS =================
const documentTypeMap = {
  panCard: "PAN",
  gstCertificate: "GST",
  fssaiCertificate: "FSSAI"
};

const documentValidators = {
  PAN: {
    required: true,
    pattern: /^[A-Z]{5}[0-9]{4}[A-Z]$/,
    message: "Invalid PAN format (ABCDE1234F)"
  },
  GST: {
    required: true,
    pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/,
    message: "Invalid GST format (15 characters)"
  },
  FSSAI: {
    required: true,
    pattern: /^[0-9]{14}$/,
    message: "FSSAI must be 14 digits"
  }
};

const bankValidators = {
  ifscCode: {
    pattern: /^[A-Z]{4}0[0-9]{6}$/,
    message: "Invalid IFSC format (SBIN0001234)"
  },
  accountNumber: {
    pattern: /^[0-9]{6,34}$/,
    message: "Account number must be 6–34 digits"
  }
};

const RestaurantDetailsModal = ({ open, onClose, data, viewMode = 'edit' }) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [editData, setEditData] = useState({});
  const [validationError, setValidationError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [restaurantImages, setRestaurantImages] = useState([]);
  const [docUploads, setDocUploads] = useState({});
  const [docDataEdits, setDocDataEdits] = useState({});
  const [cuisineSearch, setCuisineSearch] = useState('');
  const [timeSlots, setTimeSlots] = useState([{ open: '', close: '' }]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadSuccess, setUploadSuccess] = useState({});
  const tlActionMutation = useTLAction();
  const tlUpdateMutation = useTLUpdateRestaurant();

  const availableCuisines = [
    "North Indian", "South Indian", "Chinese", "Biryani", "Fast food",
    "Pizza", "Bakery", "Street Food", "Burger", "Mughlai", "Momos",
    "Sandwich", "Rolls", "Beverages", "Desserts", "Healthy Food",
    "Tea", "Coffee", "Ice-cream", "Cafe", "Shawarma", "Salad",
    "Italian", "Shake"
  ];

  useEffect(() => {
    if (data) {
      // console.log("🔄 [FRONTEND] Modal data updated:", {
      //   hasRestaurant: !!data?.restaurant,
      //   restaurantImageUrlCount: data?.restaurant?.restaurantImageUrl?.length || 0,
      //   restaurantImageUrl: data?.restaurant?.restaurantImageUrl
      // });
      
      setEditData({
        restaurantName: data?.restaurant?.restaurantName || '',
        ownerName: data?.restaurant?.ownerDetails?.name || '',
        ownerEmail: data?.restaurant?.ownerDetails?.email || '',
        ownerContact: data?.restaurant?.ownerDetails?.phoneNumber || '',
        ownerWhatsApp: data?.restaurant?.ownerDetails?.whatsappNumber || data?.restaurant?.ownerDetails?.phoneNumber || '',
        restaurantKind: data?.restaurant?.restaurantKind ?? '',
        cusineType: Array.isArray(data?.restaurant?.cusineType) ? data.restaurant.cusineType : (data?.restaurant?.cusineType ? [data.restaurant.cusineType] : []),
        dineInWaitingTime: data?.restaurant?.minWaitingTime || '',
        takeAwayWaitingTime: data?.restaurant?.minWaitingTime || '',
        restaurantAddress: data?.restaurant?.restaurantAddressDetails || data?.restaurant?.restaurantAddress?.area || '',
        accountHolderName: data?.documents?.BankDetails?.AccountHolderName || '',
        bankName: data?.documents?.BankDetails?.BankName || '',
        branchName: data?.documents?.BankDetails?.BranchName || '',
        accountNumber: data?.documents?.BankDetails?.AccountNumber || '',
        accountType: data?.documents?.BankDetails?.AccountType || '',
        ifscCode: data?.documents?.BankDetails?.IFSCCode || '',
        restaurantTime: data?.restaurant?.restaurantTime || ''
      });
      
      // Properly set restaurant images with fileUrl check
      const images = data?.restaurant?.restaurantImageUrl || [];
      const filteredImages = images.filter(img => img && img.fileUrl);
      // console.log("🖼️ [FRONTEND] Setting initial images:", {
      //   totalImages: images.length,
      //   filteredImages: filteredImages.length,
      //   images: filteredImages
      // });
      setRestaurantImages(filteredImages);
      
      if (data?.restaurant?.restaurantTime) {
        const timeMatch = data.restaurant.restaurantTime.match(/(\d{1,2}:\d{2}\s*[AP]M)\s*-\s*(\d{1,2}:\d{2}\s*[AP]M)/i);
        if (timeMatch) {
          setTimeSlots([{ open: timeMatch[1].trim(), close: timeMatch[2].trim() }]);
        }
      }
    }
  }, [data]);

  if (!open) return null;

  const handleReject = () => {
    setShowRejectModal(true);
  };

  const handleRejectClose = () => {
    setShowRejectModal(false);
  };

  const handleRejectConfirm = (reason) => {
    tlActionMutation.mutate({ 
      applicationId: data?.application?._id, 
      action: 'REJECT', 
      rejectionReason: reason 
    }, {
      onSuccess: () => {
        setShowRejectModal(false);
        onClose();
      }
    });
  };

  const handleAccept = () => {
    tlActionMutation.mutate({ 
      applicationId: data?.application?._id, 
      action: 'APPROVE' 
    }, {
      onSuccess: () => {
        onClose();
      }
    });
  };

  const handleInputChange = (field, value) => {
    let formattedValue = value;
    
    if (field === 'ifscCode') {
      formattedValue = formatIfscCode(value);
    } else if (field === 'accountNumber') {
      formattedValue = formatAccountNumber(value);
    }
    
    setEditData(prev => ({ ...prev, [field]: formattedValue }));
    setFieldErrors(prev => ({ ...prev, [field]: null }));
    setHasChanges(true);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    const errors = validateFields();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    
    if (editData.dineInWaitingTime !== editData.takeAwayWaitingTime) {
      setValidationError('Dine In and Take Away waiting times must be the same');
      return;
    }
    setValidationError('');
    setFieldErrors({});

    const formData = new FormData();
    formData.append('applicationId', data?.application?._id);
    
    const restaurantData = {
      restaurantName: editData.restaurantName,
      restaurantKind: editData.restaurantKind,
      cusineType: Array.isArray(editData.cusineType) ? editData.cusineType : [editData.cusineType],
      minWaitingTime: editData.dineInWaitingTime,
      restaurantTime: editData.restaurantTime,
      restaurantAddressDetails: editData.restaurantAddress
    };
    formData.append('restaurantData', JSON.stringify(restaurantData));

    const ownerData = {
      name: editData.ownerName
    };
    formData.append('ownerData', JSON.stringify(ownerData));

    const bankDetails = {
      AccountHolderName: editData.accountHolderName,
      BankName: editData.bankName,
      BranchName: editData.branchName,
      AccountNumber: editData.accountNumber,
      AccountType: editData.accountType,
      IFSCCode: editData.ifscCode
    };
    formData.append('bankDetails', JSON.stringify(bankDetails));

    // Only append new File objects (not existing images with fileUrl)
    const newImages = restaurantImages.filter(img => img instanceof File);
    newImages.forEach((img) => {
      formData.append('restaurantImages', img);
    });

    Object.keys(docUploads).forEach(docType => {
      if (docUploads[docType]) {
        formData.append('documents', docUploads[docType]);
        formData.append('documentTypes', docType);
      }
    });

    const docDataUpdates = {};
    Object.keys(docDataEdits).forEach(docType => {
      docDataUpdates[docType] = docDataEdits[docType];
    });
    if (Object.keys(docDataUpdates).length > 0) {
      formData.append('docDataUpdates', JSON.stringify(docDataUpdates));
    }

    tlUpdateMutation.mutate(formData, {
      onSuccess: (response) => {
        console.log("✅ [FRONTEND] Update success, response:", response.data);
        
        const updatedRestaurant = response.data.data.restaurant;
        
        // console.log("🔍 [FRONTEND] Updated restaurant:", {
        //   hasRestaurant: !!updatedRestaurant,
        //   restaurantImageUrlCount: updatedRestaurant?.restaurantImageUrl?.length || 0,
        //   restaurantImageUrl: updatedRestaurant?.restaurantImageUrl
        // });

        // Update local state with new restaurant images
        if (updatedRestaurant?.restaurantImageUrl) {
          const filteredImages = updatedRestaurant.restaurantImageUrl.filter(img => img?.fileUrl);
          // console.log("🖼️ [FRONTEND] Setting restaurant images:", {
          //   totalImages: updatedRestaurant.restaurantImageUrl.length,
          //   filteredImages: filteredImages.length,
          //   images: filteredImages
          // });
          setRestaurantImages(filteredImages);
        }

        // Update editData with latest restaurant info
        setEditData(prev => ({
          ...prev,
          restaurantName: updatedRestaurant?.restaurantName || prev.restaurantName,
          ownerName: updatedRestaurant?.ownerDetails?.name || prev.ownerName,
          restaurantKind: updatedRestaurant?.restaurantKind ?? prev.restaurantKind,
          cusineType: Array.isArray(updatedRestaurant?.cusineType) ? updatedRestaurant.cusineType : prev.cusineType,
          minWaitingTime: updatedRestaurant?.minWaitingTime || prev.minWaitingTime,
          restaurantTime: updatedRestaurant?.restaurantTime || prev.restaurantTime,
          restaurantAddress: updatedRestaurant?.restaurantAddressDetails || prev.restaurantAddress
        }));

        setIsEditing(false);
        setHasChanges(false);
        setValidationError('');
        setDocUploads({});
        setDocDataEdits({});
        
        setTimeout(() => {
          onClose();
        }, 300);
      }
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setHasChanges(false);
    setValidationError('');
    setFieldErrors({});
    setDocUploads({});
    setDocDataEdits({});
    setRestaurantImages(data?.restaurant?.restaurantImageUrl?.filter(img => img.fileUrl) || []);
    setEditData({
      restaurantName: data?.restaurant?.restaurantName || '',
      ownerName: data?.restaurant?.ownerDetails?.name || '',
      ownerEmail: data?.restaurant?.ownerDetails?.email || '',
      ownerContact: data?.restaurant?.ownerDetails?.phoneNumber || '',
      ownerWhatsApp: data?.restaurant?.ownerDetails?.whatsappNumber || data?.restaurant?.ownerDetails?.phoneNumber || '',
      restaurantKind: data?.restaurant?.restaurantKind ?? '',
      cusineType: Array.isArray(data?.restaurant?.cusineType) ? data.restaurant.cusineType : (data?.restaurant?.cusineType ? [data.restaurant.cusineType] : []),
      dineInWaitingTime: data?.restaurant?.minWaitingTime || '',
      takeAwayWaitingTime: data?.restaurant?.minWaitingTime || '',
      restaurantAddress: data?.restaurant?.restaurantAddressDetails || data?.restaurant?.restaurantAddress?.area || '',
      accountHolderName: data?.documents?.BankDetails?.AccountHolderName || '',
      bankName: data?.documents?.BankDetails?.BankName || '',
      branchName: data?.documents?.BankDetails?.BranchName || '',
      accountNumber: data?.documents?.BankDetails?.AccountNumber || '',
      accountType: data?.documents?.BankDetails?.AccountType || '',
      ifscCode: data?.documents?.BankDetails?.IFSCCode || '',
      restaurantTime: data?.restaurant?.restaurantTime || ''
    });
    
    if (data?.restaurant?.restaurantTime) {
      const timeMatch = data.restaurant.restaurantTime.match(/(\d{1,2}:\d{2}\s*[AP]M)\s*-\s*(\d{1,2}:\d{2}\s*[AP]M)/i);
      if (timeMatch) {
        setTimeSlots([{ open: timeMatch[1].trim(), close: timeMatch[2].trim() }]);
      }
    } else {
      setTimeSlots([{ open: '', close: '' }]);
    }
  };

  const convertTo24Hour = (time12h) => {
    if (!time12h) return '';
    const match = time12h.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!match) return '';
    let [_, hours, minutes, period] = match;
    hours = parseInt(hours);
    if (period.toUpperCase() === 'PM' && hours !== 12) hours += 12;
    if (period.toUpperCase() === 'AM' && hours === 12) hours = 0;
    return `${hours.toString().padStart(2, '0')}:${minutes}`;
  };

  const handleTimeChange = (type, index, value) => {
    if (value) {
      const [hours, minutes] = value.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      const formattedTime = `${displayHour}:${minutes} ${ampm}`;
      
      const newSlots = [...timeSlots];
      newSlots[index][type] = formattedTime;
      setTimeSlots(newSlots);
      
      const timeString = `${newSlots[0].open} - ${newSlots[0].close}`;
      handleInputChange('restaurantTime', timeString);
    }
  };

  const getAllDocuments = () => data?.documents?.documentLink || [];

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (restaurantImages.length + files.length > 5) {
      setValidationError('Maximum 5 restaurant images allowed');
      return;
    }
    setRestaurantImages([...restaurantImages, ...files]);
    setHasChanges(true);
  };

  const handleRemoveImage = (index) => {
    setRestaurantImages(restaurantImages.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const handleDocUpload = (docType, file) => {
    if (!file) return;

    // ✅ Store the file IMMEDIATELY so it is available when Save is clicked,
    // regardless of how fast the user proceeds.
    setDocUploads(prev => ({ ...prev, [docType]: file }));
    setHasChanges(true);

    // Visual progress-bar animation (purely cosmetic)
    setUploadProgress(prev => ({ ...prev, [docType]: 0 }));
    setUploadSuccess(prev => ({ ...prev, [docType]: false }));

    const interval = setInterval(() => {
      setUploadProgress(prev => {
        const current = prev[docType] || 0;
        if (current >= 100) {
          clearInterval(interval);
          setUploadSuccess(prev => ({ ...prev, [docType]: true }));
          setTimeout(() => {
            setUploadProgress(prev => ({ ...prev, [docType]: undefined }));
            setUploadSuccess(prev => ({ ...prev, [docType]: false }));
          }, 2000);
          return prev;
        }
        return { ...prev, [docType]: current + 10 };
      });
    }, 100);
  };

  const handleAddCuisine = (cuisine) => {
    if (cuisine && !editData.cusineType.some(c => c.toLowerCase() === cuisine.toLowerCase())) {
      handleInputChange('cusineType', [...editData.cusineType, cuisine]);
    }
  };

  const handleRemoveCuisine = (cuisine) => {
    handleInputChange('cusineType', editData.cusineType.filter(c => c.toLowerCase() !== cuisine.toLowerCase()));
  };

  const handleDocDataChange = (docType, value) => {
    const upperValue = value.toUpperCase();

    setDocDataEdits(prev => ({
      ...prev,
      [docType]: upperValue
    }));

    // Live validation
    const mappedType = documentTypeMap[docType];
    const validator = documentValidators[mappedType];
    let error = '';

    if (validator) {
      if (validator.required && !upperValue) {
        error = `${mappedType} is required`;
      } else if (upperValue && !validator.pattern.test(upperValue)) {
        error = validator.message;
      }
    }

    setFieldErrors(prev => ({
      ...prev,
      [docType]: error
    }));

    setHasChanges(true);
  };

  const validateFields = () => {
    const errors = {};

    // ===== Validate Documents =====
    getAllDocuments().forEach(doc => {
      const value = docDataEdits[doc.fileType] ?? doc.docData ?? '';
      const mappedType = documentTypeMap[doc.fileType];
      const validator = documentValidators[mappedType];

      if (validator) {
        if (validator.required && !value) {
          errors[doc.fileType] = `${mappedType} is required`;
        } else if (value && !validator.pattern.test(value)) {
          errors[doc.fileType] = validator.message;
        }
      }
    });

    // ===== Validate Bank Fields =====
    Object.keys(bankValidators).forEach(field => {
      const value = editData[field];
      const validator = bankValidators[field];

      if (value && !validator.pattern.test(value)) {
        errors[field] = validator.message;
      }
    });

    return errors;
  };

  const formatIfscCode = (value) => {
    const upperValue = value.toUpperCase();
    if (upperValue.length > 11) return editData.ifscCode;
    
    const index = upperValue.length - 1;
    const char = upperValue[index];
    
    if (index < 4 && !/[A-Z]/.test(char)) return editData.ifscCode;
    if (index === 4 && char !== '0') return editData.ifscCode;
    if (index > 4 && !/[0-9]/.test(char)) return editData.ifscCode;
    
    return upperValue;
  };

  const formatAccountNumber = (value) => {
    if (!/^[0-9]*$/.test(value)) return editData.accountNumber;
    if (value.length > 34) return editData.accountNumber;
    return value;
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container">

        {/* Header */}
        <div className="modal-header">
          <h3>Restaurant Details</h3>
          <button className="close-btn" onClick={onClose}>
            <img src={close} alt="" />
          </button>
        </div>

        {/* Body Content */}
        <div className="modal-body">

          {/* Left Column */}
          <div className="modal-column">
            <p><strong>Category :</strong> 
              {isEditing ? (
                <select 
                  value={editData.restaurantKind} 
                  onChange={(e) => handleInputChange('restaurantKind', Number(e.target.value))}
                  style={{marginLeft: '10px', padding: '4px', border: '1px solid #ccc', borderRadius: '4px'}}
                >
                  <option value={0}>Takeaway & Dine In</option>
                  <option value={1}>Take Away</option>
                  <option value={2}>Dine In</option>
                </select>
              ) : (
                data?.restaurant?.restaurantKind === 0 ? 'Takeaway & Dine In' : data?.restaurant?.restaurantKind === 1 ? 'Take Away' : 'Dine In'
              )}
            </p>
            
            <p><strong>Restaurant Name :</strong> 
              {isEditing ? (
                <input 
                  type="text" 
                  value={editData.restaurantName} 
                  onChange={(e) => handleInputChange('restaurantName', e.target.value)}
                  style={{marginLeft: '10px', padding: '4px', border: '1px solid #ccc', borderRadius: '4px'}}
                />
              ) : (
                editData.restaurantName || 'N/A'
              )}
            </p>
            
            <p><strong>Owner Name :</strong> 
              {isEditing ? (
                <input 
                  type="text" 
                  value={editData.ownerName} 
                  onChange={(e) => handleInputChange('ownerName', e.target.value)}
                  style={{marginLeft: '10px', padding: '4px', border: '1px solid #ccc', borderRadius: '4px'}}
                />
              ) : (
                editData.ownerName || 'N/A'
              )}
            </p>
            
            <p><strong>Owner Email :</strong> {editData.ownerEmail || 'N/A'}</p>
            <p><strong>Owner Contact No :</strong> {editData.ownerContact || 'N/A'}</p>
            <p><strong>Owner WhatsApp No :</strong> {editData.ownerWhatsApp || 'N/A'}</p>

            <p><strong>Restaurant Image :</strong> 
              {isEditing ? (
                <div style={{marginLeft: '10px', marginTop: '5px'}}>
                  {restaurantImages.map((img, index) => (
                    <span key={index} style={{marginRight: '10px', display: 'inline-block', position: 'relative'}}>
                      <a href={img.fileUrl || URL.createObjectURL(img)} target="_blank" rel="noopener noreferrer">
                        Img {index + 1}
                      </a>
                      <button 
                        onClick={() => handleRemoveImage(index)}
                        style={{marginLeft: '5px', cursor: 'pointer', background: 'white', color: 'black', border: '1px solid #ccc', borderRadius: '50px', padding: '2px 5px'}}
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                  {restaurantImages.length < 5 && (
                    <label style={{cursor: 'pointer', color: '#ff9d22', textDecoration: 'underline'}}>
                      + Upload
                      <input type="file" accept="image/*" multiple onChange={handleImageUpload} style={{display: 'none'}} />
                    </label>
                  )}
                </div>
              ) : (
                restaurantImages?.some(img => img?.fileUrl) ? (
                  restaurantImages.map((img, index) => (
                    <span key={index}>
                      <a href={img.fileUrl} target="_blank" rel="noopener noreferrer">
                        Img {index + 1}
                      </a>
                      {index < restaurantImages.length - 1 && ' | '}
                    </span>
                  ))
                ) : (
                  <span>Not Available</span>
                )
              )}
            </p>
            
            {data?.restaurant?.menuDetails && data.restaurant.menuDetails.length > 0 ? (
              data.restaurant.menuDetails.map((menu, index) => (
                <p key={index}><strong>{menu.menuType || 'Menu'} :</strong> 
                  {menu.signedUrl || menu.docUrl ? (
                    <a href={menu.signedUrl || menu.docUrl} target="_blank" rel="noopener noreferrer">View</a>
                  ) : (
                    <span>Not Available</span>
                  )}
                </p>
              ))
            ) : (
              <p><strong>Menu :</strong> <span>Not Available</span></p>
            )}

            <p><strong>Cuisines Category :</strong> 
              {isEditing ? (
                <div style={{marginLeft: '10px', marginTop: '5px'}}>
                  <input 
                    type="text" 
                    placeholder="Search cuisines..."
                    value={cuisineSearch}
                    onChange={(e) => setCuisineSearch(e.target.value)}
                    style={{width: '100%', padding: '6px', marginBottom: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px'}}
                  />
                  <div style={{marginBottom: '8px', display: 'flex', flexWrap: 'wrap', gap: '5px'}}>
                    {availableCuisines.filter(c => c.toLowerCase().includes(cuisineSearch.toLowerCase())).map((cuisine) => {
                      const isSelected = Array.isArray(editData.cusineType) && editData.cusineType.some(c => c.toLowerCase() === cuisine.toLowerCase());
                      return (
                        <span 
                          key={cuisine}
                          onClick={() => isSelected ? handleRemoveCuisine(cuisine) : handleAddCuisine(cuisine)}
                          style={{
                            display: 'inline-block',
                            padding: '4px 10px',
                            background: isSelected ? '#FEEDD9' : 'white',
                            color: isSelected ? '#F8951D' : '#737373',
                            border: `1px solid ${isSelected ? '#F8951D' : '#737373'}`,
                            borderRadius: '12px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            transition: 'all 0.2s'
                          }}
                        >
                          {cuisine}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ) : (
                Array.isArray(editData.cusineType) ? editData.cusineType.join(', ') : (editData.cusineType || 'N/A')
              )}
            </p>

            <p><strong>Dine In Waiting Time :</strong> 
              {isEditing ? (
                <input 
                  type="number" 
                  value={editData.dineInWaitingTime} 
                  onChange={(e) => handleInputChange('dineInWaitingTime', e.target.value)}
                  style={{marginLeft: '10px', padding: '4px', border: '1px solid #ccc', borderRadius: '4px', width: '80px'}}
                />
              ) : (
                editData.dineInWaitingTime ? `${editData.dineInWaitingTime} mins` : 'N/A'
              )}
            </p>
            
            <p><strong>Take Away Waiting Time :</strong> 
              {isEditing ? (
                <input 
                  type="number" 
                  value={editData.takeAwayWaitingTime} 
                  onChange={(e) => handleInputChange('takeAwayWaitingTime', e.target.value)}
                  style={{marginLeft: '10px', padding: '4px', border: '1px solid #ccc', borderRadius: '4px', width: '80px'}}
                />
              ) : (
                editData.takeAwayWaitingTime ? `${editData.takeAwayWaitingTime} mins` : 'N/A'
              )}
            </p>
            {validationError && <p style={{color: 'red', fontSize: '12px'}}>{validationError}</p>}

            {getAllDocuments().length > 0 ? (
              getAllDocuments().map((doc, index) => (
                <p key={index}>
                  <strong>{doc.fileType || 'Document'} :</strong> 
                  {isEditing && doc.fileType !== 'cancelledCheque' ? (
                    <>
                      <input 
                        type="text" 
                        value={docDataEdits[doc.fileType] !== undefined ? docDataEdits[doc.fileType] : (doc.docData || '')}
                        onChange={(e) => handleDocDataChange(doc.fileType, e.target.value)}
                        style={{marginLeft: '5px', padding: '4px', border: `1px solid ${fieldErrors[doc.fileType] ? 'red' : '#ccc'}`, borderRadius: '4px', width: '150px'}}
                      />
                      {fieldErrors[doc.fileType] && <div style={{color: 'red', fontSize: '11px', marginLeft: '5px', marginTop: '2px'}}>{fieldErrors[doc.fileType]}</div>}
                    </>
                  ) : (
                    doc.docData || 'N/A'
                  )} /
                  {docUploads[doc.fileType] ? (
                    <a href={URL.createObjectURL(docUploads[doc.fileType])} target="_blank" rel="noopener noreferrer"> View</a>
                  ) : doc.signedUrl ? (
                    <a href={doc.signedUrl} target="_blank" rel="noopener noreferrer"> View</a>
                  ) : (
                    <span> Not Available</span>
                  )}
                  {isEditing && (
                    <>
                      <label style={{marginLeft: '10px', cursor: 'pointer', color: '#ff9d22', textDecoration: 'underline'}}>
                        Re-upload
                        <input 
                          type="file" 
                          accept=".pdf,.jpg,.jpeg,.png" 
                          onChange={(e) => handleDocUpload(doc.fileType, e.target.files[0])} 
                          style={{display: 'none'}} 
                        />
                      </label>
                      {uploadProgress[doc.fileType] !== undefined && (
                        <div style={{marginLeft: '10px', marginTop: '5px', width: '200px'}}>
                          <div style={{width: '100%', height: '6px', background: '#e0e0e0', borderRadius: '3px', overflow: 'hidden'}}>
                            <div style={{width: `${uploadProgress[doc.fileType]}%`, height: '100%', background: '#4caf50', transition: 'width 0.1s'}}></div>
                          </div>
                        </div>
                      )}
                      {uploadSuccess[doc.fileType] && (
                        <span style={{marginLeft: '10px', color: '#4caf50', fontSize: '12px'}}>✓ Upload successful!</span>
                      )}
                    </>
                  )}
                </p>
              ))
            ) : (
              <p><strong>Documents :</strong> <span>Not Available</span></p>
            )}

            <p>
              <strong>Restaurant/FSSAI Address :</strong> 
              {isEditing ? (
                <textarea 
                  value={editData.restaurantAddress} 
                  onChange={(e) => handleInputChange('restaurantAddress', e.target.value)}
                  style={{marginLeft: '10px', padding: '4px', border: '1px solid #ccc', borderRadius: '4px', width: '200px', height: '60px'}}
                />
              ) : (
                editData.restaurantAddress || 'N/A'
              )}
            </p>
          </div>

          {/* Right Column */}
          <div className="modal-column">
            <p><strong>Account Holder Name :</strong> 
              {isEditing ? (
                <input 
                  type="text" 
                  value={editData.accountHolderName} 
                  onChange={(e) => handleInputChange('accountHolderName', e.target.value)}
                  style={{marginLeft: '10px', padding: '4px', border: '1px solid #ccc', borderRadius: '4px'}}
                />
              ) : (
                editData.accountHolderName || 'N/A'
              )}
            </p>
            
            <p><strong>Bank Name :</strong> 
              {isEditing ? (
                <input 
                  type="text" 
                  value={editData.bankName} 
                  onChange={(e) => handleInputChange('bankName', e.target.value)}
                  style={{marginLeft: '10px', padding: '4px', border: '1px solid #ccc', borderRadius: '4px'}}
                />
              ) : (
                editData.bankName || 'N/A'
              )}
            </p>
            
            <p><strong>Branch Name :</strong> 
              {isEditing ? (
                <input 
                  type="text" 
                  value={editData.branchName} 
                  onChange={(e) => handleInputChange('branchName', e.target.value)}
                  style={{marginLeft: '10px', padding: '4px', border: '1px solid #ccc', borderRadius: '4px'}}
                />
              ) : (
                editData.branchName || 'N/A'
              )}
            </p>
            
            <p><strong>Account Number :</strong> 
              {isEditing ? (
                <div style={{display: 'inline-block'}}>
                  <input 
                    type="text" 
                    value={editData.accountNumber} 
                    onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                    style={{marginLeft: '10px', padding: '4px', border: `1px solid ${fieldErrors.accountNumber ? 'red' : '#ccc'}`, borderRadius: '4px'}}
                  />
                  {fieldErrors.accountNumber && <div style={{color: 'red', fontSize: '11px', marginLeft: '10px', marginTop: '2px'}}>{fieldErrors.accountNumber}</div>}
                </div>
              ) : (
                editData.accountNumber || 'N/A'
              )}
            </p>
            
            <p><strong>Account Type :</strong> 
              {isEditing ? (
                <select 
                  value={editData.accountType} 
                  onChange={(e) => handleInputChange('accountType', e.target.value)}
                  style={{marginLeft: '10px', padding: '4px', border: '1px solid #ccc', borderRadius: '4px'}}
                >
                  <option value="">Select Type</option>
                  <option value="Savings">Savings</option>
                  <option value="Current">Current</option>
                </select>
              ) : (
                editData.accountType || 'N/A'
              )}
            </p>
            
            <p><strong>IFSC Code :</strong> 
              {isEditing ? (
                <div style={{display: 'inline-block'}}>
                  <input 
                    type="text" 
                    value={editData.ifscCode} 
                    onChange={(e) => handleInputChange('ifscCode', e.target.value)}
                    style={{marginLeft: '10px', padding: '4px', border: `1px solid ${fieldErrors.ifscCode ? 'red' : '#ccc'}`, borderRadius: '4px'}}
                  />
                  {fieldErrors.ifscCode && <div style={{color: 'red', fontSize: '11px', marginLeft: '10px', marginTop: '2px'}}>{fieldErrors.ifscCode}</div>}
                </div>
              ) : (
                editData.ifscCode || 'N/A'
              )}
            </p>

            <br />

            <p><strong>Restaurant Timing</strong></p>

            {isEditing ? (
              <div style={{marginLeft: '10px', marginTop: '10px'}}>
                {timeSlots.map((slot, index) => (
                  <div key={index} style={{marginBottom: '15px'}}>
                    <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                      <input 
                        type="time"
                        value={convertTo24Hour(slot.open)}
                        onChange={(e) => handleTimeChange('open', index, e.target.value)}
                        onClick={(e) => e.target.showPicker()} // For mobile devices
                        style={{
                          flex: 1,
                          padding: '8px 10px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      />
                      <span style={{color: '#666'}}>-</span>
                      <input 
                        type="time"
                        value={convertTo24Hour(slot.close)}
                        onChange={(e) => handleTimeChange('close', index, e.target.value)}
                        onClick={(e) => e.target.showPicker()}
                        style={{
                          flex: 1,
                          padding: '8px 10px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              editData.restaurantTime ? (
                <label>
                  All Days ({editData.restaurantTime})
                </label>
              ) : (
                ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                  <label key={day}>
                    {day} (Not Available)
                  </label>
                ))
              )
            )}
          </div>
        </div>

        {/* Footer Buttons */}
        {viewMode === 'edit' && (
          <div className="modal-footer">
            {isEditing ? (
              <>
                <button className="reject-btn" onClick={handleCancel} disabled={tlUpdateMutation.isPending}>Cancel</button>
                <button className="approve-btn" onClick={handleSave} disabled={tlUpdateMutation.isPending || !hasChanges}>
                  {tlUpdateMutation.isPending ? 'Saving...' : 'Save'}
                </button>
              </>
            ) : (
              <>
                <button className="reject-btn" onClick={handleReject} disabled={tlActionMutation.isPending}>Reject</button>
                <button className="edit-btn" onClick={handleEdit} style={{backgroundColor: 'white', color: '#ff9d22', padding: '10px 70px', border: '1px solid #ff9d22', borderRadius: '10px', cursor: 'pointer'}}>Edit</button>
                <button className="approve-btn" onClick={handleAccept} disabled={tlActionMutation.isPending}>
                  {tlActionMutation.isPending ? 'Processing...' : 'Approve'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
      
      {showRejectModal && (
        <RejectApplicationModal 
          onClose={handleRejectClose}
          onReject={handleRejectConfirm}
        />
      )}
    </div>
  );
};

export default RestaurantDetailsModal;
